import React, { useState, useEffect, useCallback } from "react";
import env from "../../config/env.js";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, Tooltip, Select, MenuItem, InputLabel, FormControl, Snackbar, Alert,
  TablePagination, CircularProgress, Grid
} from "@mui/material";
import { Visibility as VisibilityIcon, Assessment as AssessmentIcon, CheckCircle as CheckCircleIcon } from "@mui/icons-material";
import api from "../../services/api";
import AcessoRestrito from '../../components/AcessoRestrito';
import { useSystemMessagesContext } from "../../contexts/SystemMessagesContext";

const drawerWidth = 280;

export default function Turmas() {
  
  // Estados principais
  const [turmas, setTurmas] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openPresenca, setOpenPresenca] = useState(false);
  const [openChamada, setOpenChamada] = useState(false);
  const [openRelatorio, setOpenRelatorio] = useState(false);
  const [turmaSelecionada, setTurmaSelecionada] = useState(null);
  
  // Estados para registro de presença
  const [clientesTurma, setClientesTurma] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [presencaData, setPresencaData] = useState({
    id_cliente: '',
    data: '',
    avaliacoes: ['']
  });
  const [loadingPresenca, setLoadingPresenca] = useState(false);
  
  const [filtros, setFiltros] = useState({
    codigo: '',
    cliente: '',
    colaborador: '',
    turma: '',
    diaSemana: '',
    horario: ''
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Função para criar logs de auditoria
  // Função de log de auditoria removida - agora é feita no backend

  const carregarColaboradores = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const codHashEmpresa = localStorage.getItem("st_codHashEmpresa");
      const headers = {
        'Authorization': `Bearer ${token}`,
        'x-second-key': env.SECOND_SECRET_KEY
      };
      const res = await api.get(`/colaboradores?st_codHashEmpresa=${codHashEmpresa}`, { headers });
      setColaboradores(res.data || []);
    } catch (err) {
      console.error("Erro ao carregar colaboradores:", err);
    }
  }, []);

  // Carregar turmas (baseado em horarioAtendimento)
  const carregarTurmas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const codHashEmpresa = localStorage.getItem("st_codHashEmpresa");
      const headers = {
        'Authorization': `Bearer ${token}`,
        'x-second-key': env.SECOND_SECRET_KEY
      };
      // TODO: Ajustar endpoint conforme backend
      const res = await api.get(`/horarioAtendimento?st_codHashEmpresa=${codHashEmpresa}`, { headers });
      setTurmas(res.data || []);
    } catch (err) {
      setError("Erro ao carregar turmas");
    } finally {
      setLoading(false);
    }
  }, []);

  // Carregar clientes matriculados na turma
  const carregarClientesTurma = useCallback(async (codigoTurma) => {
    if (!codigoTurma) return;
    
    setLoadingClientes(true);
    try {
      const token = localStorage.getItem("token");
      const headers = {
        'Authorization': `Bearer ${token}`,
        'x-second-key': env.SECOND_SECRET_KEY
      };
      
      // Buscar clientes matriculados na turma
      const res = await api.get(`/turmas/lista-chamada/${codigoTurma}`, { headers });
      
      if (res.data && res.data.alunos) {
        // Filtrar apenas alunos matriculados
        const matriculados = res.data.alunos.filter(aluno => aluno.status === 'Matriculado');
        setClientesTurma(matriculados);
      } else {
        setClientesTurma([]);
      }
    } catch (err) {
      console.error("Erro ao carregar clientes da turma:", err);
      setError("Erro ao carregar clientes da turma");
      setClientesTurma([]);
    } finally {
      setLoadingClientes(false);
    }
  }, []);

  useEffect(() => {
    carregarTurmas();
    carregarColaboradores();
  }, [carregarTurmas, carregarColaboradores]);

  // Função para obter nome do colaborador
  const getNomeColaborador = (colaboradorId) => {
    const colaborador = colaboradores.find(col => col.id === colaboradorId || col._id === colaboradorId);
    return colaborador ? colaborador.nome_completo : colaboradorId;
  };

  // Função para converter números dos dias da semana em nomes
  const getDiasSemana = (diasNumeros) => {
    if (!Array.isArray(diasNumeros)) return '';
    
    const diasSemana = {
      0: 'Domingo',
      1: 'Segunda-feira',
      2: 'Terça-feira', 
      3: 'Quarta-feira',
      4: 'Quinta-feira',
      5: 'Sexta-feira',
      6: 'Sábado'
    };
    
    return diasNumeros
      .map(dia => diasSemana[dia] || `Dia ${dia}`)
      .join(', ');
  };

  // Filtros
  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({ ...prev, [name]: value }));
    setPage(0);
  };

  // Ações principais
  const handleAbrirPresenca = async (turma) => {
    setTurmaSelecionada(turma);
    setOpenPresenca(true);
    
    // Resetar dados de presença
    setPresencaData({
      id_cliente: '',
      data: '',
      avaliacoes: ['']
    });
    
    // Carregar clientes da turma
    await carregarClientesTurma(turma.codigoTurma);
    
    // Log de auditoria removido - agora é feito no backend
  };
  
  const handleAbrirChamada = (turma) => {
    setTurmaSelecionada(turma);
    setOpenChamada(true);
    // Log de auditoria removido - agora é feito no backend
  };
  
  const handleAbrirRelatorio = () => {
    setOpenRelatorio(true);
    // Log de auditoria removido - agora é feito no backend
  };

  // Funções para registro de presença
  const handlePresencaChange = (field, value) => {
    setPresencaData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAvaliacaoChange = (index, value) => {
    const novasAvaliacoes = [...presencaData.avaliacoes];
    novasAvaliacoes[index] = value;
    
    // Adicionar nova avaliação se necessário (máximo 6)
    if (index === novasAvaliacoes.length - 1 && value && novasAvaliacoes.length < 6) {
      novasAvaliacoes.push('');
    }
    
    setPresencaData(prev => ({
      ...prev,
      avaliacoes: novasAvaliacoes
    }));
  };

  const removerAvaliacao = (index) => {
    const novasAvaliacoes = presencaData.avaliacoes.filter((_, i) => i !== index);
    setPresencaData(prev => ({
      ...prev,
      avaliacoes: novasAvaliacoes.length > 0 ? novasAvaliacoes : ['']
    }));
  };

  const registrarPresenca = async () => {
    if (!presencaData.id_cliente || !presencaData.data) {
      setError("Por favor, selecione o cliente e a data");
      return;
    }

    setLoadingPresenca(true);
    try {
      const token = localStorage.getItem("token");
      const headers = {
        'Authorization': `Bearer ${token}`,
        'x-second-key': env.SECOND_SECRET_KEY
      };

      // Filtrar avaliações válidas (não vazias)
      const avaliacoesValidas = presencaData.avaliacoes
        .filter(av => av !== '' && av !== null && av !== undefined)
        .map(av => parseInt(av));

      const dadosPresenca = {
        id_cliente: parseInt(presencaData.id_cliente),
        codigoTurma: turmaSelecionada.codigoTurma,
        data: presencaData.data,
        avaliacoes: avaliacoesValidas
      };

      await api.post('/turmas/presenca', dadosPresenca, { headers });
      
      setSuccess("Presença registrada com sucesso!");
      setOpenPresenca(false);
      
      // Log de auditoria removido - agora é feito no backend
      
    } catch (err) {
      console.error("Erro ao registrar presença:", err);
      setError(err.response?.data?.message || "Erro ao registrar presença");
    } finally {
      setLoadingPresenca(false);
    }
  };

  // Filtro aplicado
  const turmasFiltradas = turmas.filter(turma => {
    // TODO: Implementar lógica de filtro conforme campos disponíveis
    return true;
  });

  const turmasPaginadas = turmasFiltradas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <AcessoRestrito>
      <Box component="main" sx={{ flexGrow: 1, mt: 3, ml: { xs: 0, md: `${drawerWidth}px` }, width: { xs: "100%", md: `calc(100% - ${drawerWidth}px)` }, pr: 3, pl: 3 }}>
        <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>{error}</Alert>
        </Snackbar>
        <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>{success}</Alert>
        </Snackbar>
        <Box mb={2} display="flex" justifyContent="space-between" flexWrap="wrap">
          <Typography variant="h5" mb={1}>Turmas</Typography>
          <Button variant="outlined" onClick={handleAbrirRelatorio} startIcon={<AssessmentIcon />}>Relatórios</Button>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Turma</TableCell>
                <TableCell>Colaborador</TableCell>
                <TableCell>Dia da Semana</TableCell>
                <TableCell>Horário</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <TextField name="codigo" size="small" value={filtros.codigo} onChange={handleFiltroChange} placeholder="Filtrar código" />
                </TableCell>
                <TableCell>
                  <TextField name="turma" size="small" value={filtros.turma} onChange={handleFiltroChange} placeholder="Filtrar turma" />
                </TableCell>
                <TableCell>
                  <TextField name="colaborador" size="small" value={filtros.colaborador} onChange={handleFiltroChange} placeholder="Filtrar colaborador" />
                </TableCell>
                <TableCell>
                  <TextField name="diaSemana" size="small" value={filtros.diaSemana} onChange={handleFiltroChange} placeholder="Filtrar dia" />
                </TableCell>
                <TableCell>
                  <TextField name="horario" size="small" value={filtros.horario} onChange={handleFiltroChange} placeholder="Filtrar horário" />
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} align="center"><CircularProgress /></TableCell></TableRow>
              ) : turmasPaginadas.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center">Nenhuma turma encontrada.</TableCell></TableRow>
              ) : (
                turmasPaginadas.map((turma) => (
                  <TableRow key={turma.codigoTurma || turma._id}>
                    <TableCell>{turma.codigoTurma}</TableCell>
                    <TableCell>{turma.nome || turma.codigoTurma}</TableCell>
                    <TableCell>{getNomeColaborador(turma.colaborador)}</TableCell>
                    <TableCell>{getDiasSemana(turma.diasDaSemana)}</TableCell>
                    <TableCell>{turma.horaInicio && turma.horaFim ? `${turma.horaInicio} - ${turma.horaFim}` : turma.horaInicio}</TableCell>
                    <TableCell>
                      <Tooltip title="Registrar Presença"><IconButton onClick={() => handleAbrirPresenca(turma)}><CheckCircleIcon /></IconButton></Tooltip>
                      <Tooltip title="Lista de Chamada"><IconButton onClick={() => handleAbrirChamada(turma)}><VisibilityIcon /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={turmasFiltradas.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Itens por página"
        />
        
        {/* Dialog de Registro de Presença */}
        <Dialog open={openPresenca} onClose={() => setOpenPresenca(false)} maxWidth="md" fullWidth>
          <DialogTitle>Registrar Presença - {turmaSelecionada?.nome || turmaSelecionada?.codigoTurma}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Cliente</InputLabel>
                  <Select
                    value={presencaData.id_cliente}
                    onChange={(e) => handlePresencaChange('id_cliente', e.target.value)}
                    label="Cliente"
                    disabled={loadingClientes}
                  >
                    <MenuItem value="">
                      <em>Selecione um cliente...</em>
                    </MenuItem>
                    {clientesTurma.map((cliente) => (
                      <MenuItem key={cliente.id} value={cliente.id}>
                        {cliente.nome}
                      </MenuItem>
                    ))}
                  </Select>
                  {loadingClientes && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" sx={{ ml: 1 }}>
                        Carregando clientes...
                      </Typography>
                    </Box>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Data"
                  type="date"
                  value={presencaData.data}
                  onChange={(e) => handlePresencaChange('data', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Avaliações (0-100)
                </Typography>
                <Grid container spacing={2}>
                  {presencaData.avaliacoes.map((avaliacao, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <TextField
                        fullWidth
                        label={`Avaliação ${index + 1}`}
                        type="number"
                        value={avaliacao}
                        onChange={(e) => handleAvaliacaoChange(index, e.target.value)}
                        inputProps={{ min: 0, max: 100 }}
                        InputProps={{
                          endAdornment: presencaData.avaliacoes.length > 1 && (
                            <IconButton
                              size="small"
                              onClick={() => removerAvaliacao(index)}
                              sx={{ color: 'error.main' }}
                            >
                              ×
                            </IconButton>
                          )
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
                <Typography variant="caption" color="text.secondary">
                  Máximo 6 avaliações. Deixe em branco se não houver avaliação.
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenPresenca(false)}>Cancelar</Button>
            <Button 
              onClick={registrarPresenca} 
              variant="contained" 
              disabled={loadingPresenca || !presencaData.id_cliente || !presencaData.data}
            >
              {loadingPresenca ? <CircularProgress size={20} /> : 'Registrar Presença'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de Lista de Chamada */}
        <Dialog open={openChamada} onClose={() => setOpenChamada(false)} maxWidth="md" fullWidth>
          <DialogTitle>Lista de Chamada</DialogTitle>
          <DialogContent>
            {/* TODO: Implementar visualização da lista de chamada */}
            <Typography>Em desenvolvimento...</Typography>
          </DialogContent>
          <DialogActions><Button onClick={() => setOpenChamada(false)}>Fechar</Button></DialogActions>
        </Dialog>

        {/* Dialog de Relatório */}
        <Dialog open={openRelatorio} onClose={() => setOpenRelatorio(false)} maxWidth="lg" fullWidth>
          <DialogTitle>Relatório de Turmas</DialogTitle>
          <DialogContent>
            {/* TODO: Implementar relatório por colaborador */}
            <Typography>Em desenvolvimento...</Typography>
          </DialogContent>
          <DialogActions><Button onClick={() => setOpenRelatorio(false)}>Fechar</Button></DialogActions>
        </Dialog>
      </Box>
    </AcessoRestrito>
  );
}
